import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaCheckCircle,
  FaEnvelope,
  FaBell,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUsers,
  FaHome,
  FaClock,
  FaInfoCircle,
  FaDownload,
  FaPhone,
  FaHotel,
  FaRobot,
  FaRoute
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { downloadBookingReceipt } from '../utils/bookingReceipt';
import ReceiptPreviewModal from '../components/ReceiptPreviewModal';
import BookStayGuarantee from '../components/BookStayGuarantee';
import { TrustBadgeRow } from '../components/TrustBadge';
import './BookingConfirmationPage.css';
import '../components/BookStayGuarantee.css';
import '../components/TrustBadge.css';

const BookingConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { property, checkIn, checkOut, guests, totalPrice, formData, bookingId: passedBookingId, paymentMethod } = location.state || {};

  const [bookingStatus, setBookingStatus] = useState('pending'); // 'pending', 'approved', 'declined'
  const [emailSent, setEmailSent] = useState(false);
  const [notificationSent, setNotificationSent] = useState(false);
  const [bookingId, setBookingId] = useState(passedBookingId || '');
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => {
    // Redirect if no booking data
    if (!property || !checkIn || !checkOut) {
      navigate('/');
      return;
    }

    // Generate booking ID if not provided
    if (!passedBookingId) {
      const id = `BK${Date.now().toString().slice(-8)}`;
      setBookingId(id);
    }

    // Simulate notification status (already sent from PaymentPage)
    setTimeout(() => {
      setEmailSent(true);
      setNotificationSent(true);
    }, 500);
  }, [property, checkIn, checkOut, navigate, passedBookingId]);

  if (!property || !checkIn || !checkOut) {
    return null;
  }

  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  const numberOfNights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));

  const getSafeString = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.name) return String(value.name);
      if (value.city) return String(value.city);
      return fallback;
    }
    return String(value);
  };

  const handleDownload = () => setShowReceiptModal(true);
  const receiptBooking = {
    bookingId,
    property,
    checkIn,
    checkOut,
    guests: typeof guests === 'number' ? guests : guests?.adults || 1,
    totalPrice,
    formData,
    currency: 'INR',
    paymentMethod: paymentMethod || 'payAtHotel',
    paymentStatus: paymentMethod === 'online' ? 'paid' : null
  };

  return (
    <div className="booking-confirmation-page">
      <Navbar />
      
      <div className="confirmation-container">
        {/* Success Message - Minimized */}
        <motion.div
          className="success-message"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <FaCheckCircle className="success-icon-small" />
          <div className="success-text">
            <h1>Booking Submitted Successfully!</h1>
            <p>Your booking request has been sent to the host for confirmation</p>
          </div>
        </motion.div>

        {/* Booking Status */}
        <motion.div
          className="booking-status-card"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="status-header">
            <h2>Booking Status</h2>
            <span className={`status-badge ${bookingStatus}`}>
              {bookingStatus === 'pending' && '⏳ Pending Host Approval'}
              {bookingStatus === 'approved' && '✅ Approved'}
              {bookingStatus === 'declined' && '❌ Declined'}
            </span>
          </div>
          <div className="status-info">
            <p>
              <strong>Booking ID:</strong> {bookingId}
            </p>
            <p className="status-message">
              The host will review your booking request and respond within 24 hours. 
              You'll receive a confirmation email once the host approves your booking.
            </p>
          </div>
        </motion.div>

        <BookStayGuarantee variant="compact" className="confirmation-guarantee" />
        {property && <TrustBadgeRow property={property} limit={5} compact className="confirmation-trust-badges" />}

        {/* Notification Status */}
        <motion.div
          className="notification-status"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="notification-item">
            <div className="notification-icon email">
              <FaEnvelope />
            </div>
            <div className="notification-content">
              <h3>Email Confirmation</h3>
              {emailSent ? (
                <p className="sent">✓ Confirmation email sent to {formData?.email}</p>
              ) : (
                <p className="sending">Sending email...</p>
              )}
            </div>
          </div>

          <div className="notification-item">
            <div className="notification-icon app">
              <FaBell />
            </div>
            <div className="notification-content">
              <h3>Host Notification</h3>
              {notificationSent ? (
                <p className="sent">✓ Booking request sent to host</p>
              ) : (
                <p className="sending">Sending notification...</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Booking Details */}
        <motion.div
          className="booking-details-section"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className="section-header">
            <h2>Your Booking Details</h2>
            <button className="btn-download" onClick={handleDownload}>
              <FaDownload /> Download PDF
            </button>
          </div>

          {showReceiptModal && (
            <ReceiptPreviewModal
              booking={receiptBooking}
              onClose={() => setShowReceiptModal(false)}
              onDownload={downloadBookingReceipt}
            />
          )}

          <div className="details-grid">
            {/* Property Info */}
            <div className="detail-card">
              <div className="card-icon">
                <FaHotel />
              </div>
              <div className="card-content">
                <h3>Property</h3>
                <p className="property-name">{getSafeString(property.name)}</p>
                <p className="property-location">
                  <FaMapMarkerAlt /> {getSafeString(property.city)}, {getSafeString(property.state, 'India')}
                </p>
              </div>
            </div>

            {/* Check-in */}
            <div className="detail-card">
              <div className="card-icon">
                <FaCalendarAlt />
              </div>
              <div className="card-content">
                <h3>Check-in</h3>
                <p className="date-value">
                  {checkInDate.toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </p>
                <p className="time-value">
                  <FaClock /> 3:00 PM - 11:00 PM
                </p>
              </div>
            </div>

            {/* Your arrival time - highlighted when selected */}
            {formData?.arrivalTime && (
              <div className="detail-card arrival-time-highlight">
                <div className="card-icon">
                  <FaClock />
                </div>
                <div className="card-content">
                  <h3>Your arrival time</h3>
                  <p className="arrival-time-value">
                    {formData.arrivalTime.includes('-')
                      ? formData.arrivalTime.split('-').map(t => {
                          const [h, m] = t.trim().split(':');
                          const hour = parseInt(h, 10);
                          const ampm = hour >= 12 ? 'PM' : 'AM';
                          const h12 = hour % 12 || 12;
                          return `${h12}:${m || '00'} ${ampm}`;
                        }).join(' - ')
                      : formData.arrivalTime}
                  </p>
                </div>
              </div>
            )}

            {/* Check-out */}
            <div className="detail-card">
              <div className="card-icon">
                <FaCalendarAlt />
              </div>
              <div className="card-content">
                <h3>Check-out</h3>
                <p className="date-value">
                  {checkOutDate.toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </p>
                <p className="time-value">
                  <FaClock /> Until 11:00 AM
                </p>
              </div>
            </div>

            {/* Guests */}
            <div className="detail-card">
              <div className="card-icon">
                <FaUsers />
              </div>
              <div className="card-content">
                <h3>Guests</h3>
                <p className="guests-value">{guests} {guests === 1 ? 'Guest' : 'Guests'}</p>
                <p className="nights-value">{numberOfNights} {numberOfNights === 1 ? 'Night' : 'Nights'}</p>
              </div>
            </div>
          </div>

          {/* Guest Information */}
          <div className="guest-info-card">
            <h3>Guest Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Name:</span>
                <span className="info-value">{formData?.firstName} {formData?.lastName}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email:</span>
                <span className="info-value">{formData?.email}</span>
              </div>
              {formData?.phone && (
                <div className="info-item">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{formData.phone}</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Country:</span>
                <span className="info-value">{formData?.country}</span>
              </div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="price-summary-card">
            <h3>Price Summary</h3>
            <div className="price-breakdown">
              <div className="price-row">
                <span>₹{property.pricePerNight?.toLocaleString()} × {numberOfNights} nights</span>
                <span>₹{(property.pricePerNight * numberOfNights).toLocaleString()}</span>
              </div>
              <div className="price-row">
                <span>Taxes & Fees</span>
                <span>₹{Math.round((property.pricePerNight * numberOfNights) * 0.12).toLocaleString()}</span>
              </div>
              <div className="price-row total">
                <span>Total Amount</span>
                <span>₹{totalPrice?.toLocaleString()}</span>
              </div>
            </div>
            <div className="payment-note">
              <FaInfoCircle />
              <span>
                {paymentMethod === 'online'
                  ? 'Payment completed (paid online). Your booking is confirmed.'
                  : 'Payment will be made at the hotel during check-in'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* AI Trip Planner CTA */}
        <motion.div
          className="ai-trip-planner-section"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.85 }}
        >
          <div className="ai-trip-planner-content">
            <div className="ai-trip-planner-icon">
              <FaRobot />
            </div>
            <div className="ai-trip-planner-text">
              <h2>Plan the Rest of Your Trip with UBSA AI</h2>
              <p>
                Don&apos;t just book your stay. Let UBSA AI plan your entire trip — day-wise itinerary,
                nearby attractions, restaurants, transport options, and an estimated budget.
              </p>
            </div>
          </div>
          <button
            className="btn-ai-trip-plan"
            onClick={() =>
              navigate('/trip-plan/generate', {
                state: {
                  generateTrip: {
                    property,
                    checkIn,
                    checkOut,
                    guests: typeof guests === 'number' ? guests : guests?.adults || 1,
                    totalPrice,
                    bookingId: passedBookingId || bookingId,
                  },
                },
              })
            }
          >
            <FaRoute /> Generate AI Trip Plan
          </button>
        </motion.div>

        {/* Important Information */}
        <motion.div
          className="important-info-section"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <h2>Important Information</h2>
          <div className="info-boxes">
            <div className="info-box">
              <FaEnvelope className="box-icon" />
              <div>
                <h4>Check Your Email</h4>
                <p>We've sent a confirmation email to <strong>{formData?.email}</strong>. Please check your inbox and spam folder.</p>
              </div>
            </div>

            <div className="info-box">
              <FaBell className="box-icon" />
              <div>
                <h4>Host Approval Required</h4>
                <p>The host will review your booking and send approval/decline notification within 24 hours.</p>
              </div>
            </div>

            <div className="info-box">
              <FaPhone className="box-icon" />
              <div>
                <h4>Need Help?</h4>
                <p>Contact our 24/7 customer support if you have any questions about your booking.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="action-buttons-section"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.1 }}
        >
          <button className="btn-primary" onClick={() => navigate('/')}>
            <FaHome /> Back to Home
          </button>
          <button className="btn-secondary" onClick={() => navigate('/trips')}>
            View My Bookings
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
